import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

// Aviso flotante "Laura te recomienda": productos de la tienda que se administran
// en el panel de NUME (/perfil/productos-promo). El endpoint es público pero solo
// responde a navegadores desde los dominios de la lista blanca, así que en
// localhost devuelve 403 y el aviso simplemente no aparece.
const RECOMMENDATIONS_URL = 'https://api-nume-production.up.railway.app/api/v1/promo-products/recommendations';
// Tipos a pedir, separados por coma (p. ej. 'reporte,reporte_ia'). Vacío = todos los activos.
const TYPE_FILTER = '';

// Tiempos en ms: el primer aviso sale poco después de entrar y los siguientes
// a intervalos aleatorios para que no se sienta mecánico.
const FIRST_DELAY = [12000, 25000];
const NEXT_DELAY = [45000, 90000];
const VISIBLE_FOR = 9000;
// Si la persona cierra varios avisos en la misma sesión, dejamos de mostrarlos.
const MAX_DISMISSALS = 3;
const DISMISS_KEY = 'numerathum_laura_dismissals';

const randomBetween = ([min, max]) => min + Math.random() * (max - min);

const readDismissals = () => {
  try {
    return Number(sessionStorage.getItem(DISMISS_KEY) ?? '0') || 0;
  } catch {
    return 0;
  }
};

const writeDismissals = (value) => {
  try {
    sessionStorage.setItem(DISMISS_KEY, String(value));
  } catch {
    // sessionStorage no disponible (modo privado): solo afecta esta pestaña.
  }
};

// React no bloquea `javascript:` en href: solo aceptamos enlaces http(s).
const safeHref = (value) => {
  try {
    const url = new URL(String(value ?? '').trim());
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
};

export default function LauraRecommendsToast() {
  const [laura, setLaura] = useState(null);
  const [products, setProducts] = useState([]);
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const lastIdRef = useRef(null);
  const scheduleRef = useRef(null);
  const hideRef = useRef(null);

  useEffect(() => {
    if (readDismissals() >= MAX_DISMISSALS) return undefined;
    let cancelled = false;
    const params = new URLSearchParams();
    if (TYPE_FILTER) params.set('type', TYPE_FILTER);
    const query = params.toString();
    fetch(query ? `${RECOMMENDATIONS_URL}?${query}` : RECOMMENDATIONS_URL)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled || !json?.laura || !Array.isArray(json.data)) return;
        setLaura(json.laura);
        setProducts(json.data.filter((product) => product?.id && product?.name && safeHref(product.url)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const schedule = useCallback((range) => {
    clearTimeout(scheduleRef.current);
    if (products.length === 0 || readDismissals() >= MAX_DISMISSALS) return;
    scheduleRef.current = setTimeout(() => {
      const pool = products.length > 1 ? products.filter((product) => product.id !== lastIdRef.current) : products;
      const next = pool[Math.floor(Math.random() * pool.length)];
      lastIdRef.current = next.id;
      setCurrent(next);
      setVisible(true);
    }, randomBetween(range));
  }, [products]);

  const close = useCallback((byUser) => {
    clearTimeout(hideRef.current);
    setVisible(false);
    setHovered(false);
    if (byUser) writeDismissals(readDismissals() + 1);
    schedule(NEXT_DELAY);
  }, [schedule]);

  useEffect(() => {
    schedule(FIRST_DELAY);
    return () => clearTimeout(scheduleRef.current);
  }, [schedule]);

  // Se oculta sola tras unos segundos; mientras el cursor esté encima, espera.
  useEffect(() => {
    if (!visible || hovered) return undefined;
    hideRef.current = setTimeout(() => close(false), VISIBLE_FOR);
    return () => clearTimeout(hideRef.current);
  }, [visible, hovered, close]);

  if (!laura || !current) return null;

  const href = safeHref(current.url);
  const avatar = safeHref(laura.avatar_url);
  const image = safeHref(current.image_url);

  return (
    <div
      role="complementary"
      aria-label={current.type_label ? `Producto recomendado: ${current.type_label}` : 'Producto recomendado'}
      aria-hidden={!visible}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`fixed bottom-4 right-4 z-40 w-[calc(100vw-2rem)] max-w-[360px] sm:bottom-6 sm:right-6 lg:bottom-24 transition-all duration-500 ease-out motion-reduce:transition-none ${visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-6 opacity-0'}`}
    >
      <div className="relative">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={visible ? 0 : -1}
          onClick={() => close(false)}
          className="flex items-center gap-3 rounded-2xl border border-[#d4af37]/60 bg-white p-3 text-gray-800 shadow-xl transition hover:-translate-y-0.5 hover:shadow-2xl"
        >
          {avatar && (
            <img src={avatar} alt={laura.name} width={56} height={56} className="h-14 w-14 shrink-0 object-contain" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-morado">{laura.title}</p>
            {current.type_label && (
              <span className="mt-1 inline-flex rounded-full bg-morado/10 px-2 py-0.5 text-[11px] font-semibold leading-4 text-morado">
                {current.type_label}
              </span>
            )}
            <p className="mt-0.5 line-clamp-2 text-sm font-semibold leading-snug">{current.name}</p>
            {current.price_label && (
              <p className="mt-1 text-sm font-bold text-morado">
                {current.price_label}
                {current.regular_price_label && (
                  <s className="ml-2 text-xs font-medium text-gray-500">{current.regular_price_label}</s>
                )}
              </p>
            )}
          </div>
          {image && (
            <img src={image} alt="" width={64} height={64} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
          )}
        </a>
        <button
          type="button"
          onClick={() => close(true)}
          tabIndex={visible ? 0 : -1}
          aria-label="Cerrar recomendación"
          className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full border border-[#d4af37]/60 bg-white text-gray-500 shadow-md transition hover:bg-morado/5 hover:text-morado"
        >
          <X size={14} strokeWidth={2.5} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
